import { StatusIndicatorProps } from '@amzn/awsui-components-react';
import {
    BulkBlockedReason,
    BulkPromoteDisposition,
    BulkPromotePhase,
    BulkPromotionStatus,
    UNCHANGED_BANDS_AS_SUCCESS,
} from 'src/models/promotion/bulkPromotionEnums';

/** [label, indicator band]. Two members so status is never conveyed by colour alone. */
export type BulkBand = [string, StatusIndicatorProps.Type];

/** [label, remedy]. The remedy is the whole point of keeping the reasons distinct. */
export type BlockedReasonPresentation = [string, string];

const DISPOSITION_BANDS: Record<string, BulkBand> = {
    [BulkPromoteDisposition.INITIATED]: ['Initiated', 'in-progress'],
    [BulkPromoteDisposition.BLOCKED_AT_PREFLIGHT]: ['Blocked at preflight', 'warning'],
    [BulkPromoteDisposition.BLOCKED_AT_REVALIDATION]: ['Blocked at revalidation', 'warning'],
    // Not a failure: already matching the target is the desired outcome of a re-run.
    [BulkPromoteDisposition.SKIPPED_UNCHANGED]: ['Skipped — already up to date', 'success'],
    [BulkPromoteDisposition.SKIPPED_TARGET_AWAITING_APPROVAL]: ['Skipped — awaiting approval in target', 'info'],
    [BulkPromoteDisposition.BARRIER_FAILED]: ['Barrier failed', 'error'],
    [BulkPromoteDisposition.FAILED_TO_INITIATE]: ['Failed to initiate', 'error'],
};

const PHASE_BANDS: Record<string, BulkBand> = {
    [BulkPromotePhase.RESOLVING]: ['Resolving identifiers', 'in-progress'],
    [BulkPromotePhase.VALIDATING]: ['Validating', 'in-progress'],
    [BulkPromotePhase.BLOCKED_LIST_READY]: ['Verdict ready', 'info'],
    [BulkPromotePhase.EXECUTING_WAVE]: ['Promoting', 'in-progress'],
    // 'pending' rather than 'in-progress': a batch waiting on its masters is otherwise
    // indistinguishable from a hung one, and the instinct in that ambiguity is to resubmit.
    [BulkPromotePhase.AT_BARRIER]: ['Waiting for masters to reach the target', 'pending'],
    [BulkPromotePhase.COMPLETE]: ['Complete', 'success'],
};

const BATCH_STATUS_BANDS: Record<string, BulkBand> = {
    [BulkPromotionStatus.ACCEPTED]: ['Accepted', 'pending'],
    [BulkPromotionStatus.PREFLIGHT_RUNNING]: ['Checking components', 'in-progress'],
    // 'pending' rather than 'in-progress': nothing is running, by design, until the operator
    // confirms. Banded in-progress, a batch waiting on a person looks like one waiting on the system.
    [BulkPromotionStatus.AWAITING_CONFIRMATION]: ['Waiting for your confirmation', 'pending'],
    [BulkPromotionStatus.CONFIRMED]: ['Confirmed', 'in-progress'],
    [BulkPromotionStatus.RUNNING]: ['Running', 'in-progress'],
    // Blocked components do not make a batch unsuccessful: it did everything that could be done.
    [BulkPromotionStatus.SUCCEEDED]: ['Succeeded', 'success'],
    [BulkPromotionStatus.PARTIALLY_SUCCEEDED]: ['Partially succeeded', 'warning'],
    [BulkPromotionStatus.FAILED]: ['Failed', 'error'],
    // Not "the verdict expired": ABANDONED is written both when the operator releases a batch and when the
    // consent window lapses, and the status alone does not say which. In alpha on 2026-09-22, a8c07287 and
    // bdce33df were both released by the operator 9 and 10 minutes after submit — an hour short of expiry —
    // and each was reported as having expired. Telling an operator the system gave up when they cancelled is
    // worse than saying less. The cause is derivable from completedAt against preflightExpiresAt, but not
    // here: this map sees only the status.
    [BulkPromotionStatus.ABANDONED]: ['Abandoned', 'info'],

    // Values the service does not write. Kept so an older recorded status still bands.
    [BulkPromotionStatus.COMPLETED]: ['Completed', 'success'],
    [BulkPromotionStatus.COMPLETED_WITH_FAILURES]: ['Completed with failures', 'warning'],
    [BulkPromotionStatus.BLOCKED]: ['Blocked', 'warning'],
};

/**
 * Reason to [label, remedy]. Bands are assigned at the disposition level, so no reason carries one; these
 * drive grouping and the remedy text.
 *
 * Keyed on the seven names BulkBlockedReason.java declares, so every entry is reachable and every reason the
 * service emits has one. The previous table was keyed on a fifteen-name client vocabulary of which only
 * three matched, so the most common refusals fell through to the service's own prose and twelve authored
 * remedies were dead code.
 */
const BLOCKED_REASON_PRESENTATION: Record<string, BlockedReasonPresentation> = {
    [BulkBlockedReason.DEPENDENCY_NOT_IN_TARGET]: [
        'Dependency not in the target',
        'Promote the dependency first, then have it approved in the target.',
    ],
    [BulkBlockedReason.SOURCE_NOT_PROMOTABLE]: [
        'Not promotable as it stands',
        // The service's own message names the specific problem — no active version, or an unapproved draft —
        // and the row's detail is where it lands, so pointing at it beats guessing which of the two it was.
        'Read the detail on the row: it names what about this component blocks it, usually no active version '
        + 'or an unapproved draft.',
    ],
    [BulkBlockedReason.UNCHANGED_SINCE_LAST_PROMOTION]: [
        'Unchanged since the last promotion',
        'Nothing to do — the target already matches the source.',
    ],
    [BulkBlockedReason.IN_FLIGHT_CONFLICT]: [
        'Promotion already in flight',
        'Wait for the in-flight promotion to reach a terminal state, then re-run.',
    ],
    [BulkBlockedReason.PROMOTION_DISABLED]: [
        'Promotion is not enabled in this environment',
        'This stage cannot promote. Ask for promotion to be enabled, or use a stage where it is.',
    ],
    [BulkBlockedReason.REFUSED_AT_PROMOTION]: [
        'The promotion was refused',
        // The service reports this cause only as a message, so the row's own detail is the remedy.
        'Read the detail on the row — the promotion API reported the cause there and nowhere else.',
    ],
    [BulkBlockedReason.VALIDATION_INDETERMINATE]: [
        'Dependency validation could not be completed',
        'The check failed rather than reporting a problem. Re-run the preflight.',
    ],
};

/** Renders an unrecognised token as prose, so an unknown value degrades readably. */
export function humaniseToken(token: string): string {
    if (!token) {
        return 'Unknown';
    }
    const words = token.replace(/_/g, ' ').trim().toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

export function getDispositionBand(disposition: string): BulkBand {
    return DISPOSITION_BANDS[disposition] || [humaniseToken(disposition), 'info'];
}

export function getPhaseBand(phase: string): BulkBand {
    return PHASE_BANDS[phase] || [humaniseToken(phase), 'info'];
}

export function getBatchStatusBand(status: string): BulkBand {
    return BATCH_STATUS_BANDS[status] || [humaniseToken(status), 'info'];
}

/**
 * Falls back to the service's own detail text, then to the humanised token. A keyed lookup alone
 * would blank the cell for whichever spelling the service settled on.
 */
export function getBlockedReasonPresentation(reason: string, detail?: string): BlockedReasonPresentation {
    const known = BLOCKED_REASON_PRESENTATION[reason];
    if (known) {
        return known;
    }
    return [humaniseToken(reason), detail || 'No remedy is recorded for this reason.'];
}

/**
 * Whether a blocked reason is a failure.
 *
 * Expressed here rather than mirrored from the Java: BulkBlockedReason has no isFailure() — its only
 * methods are detail(), classify() and buildWireReasonMap() — and the not-a-failure property is stated in
 * that enum as a comment on UNCHANGED_SINCE_LAST_PROMOTION.
 *
 * Expressed as a rule rather than a per-reason flag so the single exception survives a new reason being
 * added: all are failures except UNCHANGED_SINCE_LAST_PROMOTION, where the target already matches.
 */
export function isFailureReason(reason: string): boolean {
    return reason !== UNCHANGED_BANDS_AS_SUCCESS;
}

/** Only these two dispositions are failures; the count that drives error rates. */
export function isFailureDisposition(disposition: string): boolean {
    return (
        disposition === BulkPromoteDisposition.BARRIER_FAILED ||
        disposition === BulkPromoteDisposition.FAILED_TO_INITIATE
    );
}
